import Swal from "sweetalert2";
import { apiFetcher } from "../services/API.service";

import i18n from "../i18n";
import {
  isCategory,
  isDepartment,
  isFullProduct,
  isResponseSearchFilterOptions,
  isShortProduct
} from "../utilities/dataValidators";
import { systemErrorAlertText } from "../utilities/utils";
import { fetchUserOpenAuctionsData } from "./user.model";

// Updated return type to include nextMajorAuction
export async function fetchAllProductsHomepage(
  pageSize: number,
  pageNumber: number,
  signal?: AbortSignal
): Promise<
  | {
      rStatus: "success";
      rData: {
        featuredProducts: ShortProduct[];
        products: ShortProduct[];
        nextMajorAuction?: ShortProduct;
        totalProducts: number;
      };
    }
  | { rStatus: "error"; message: string }
> {
  try {
    console.log(pageNumber, pageSize);
    const response = await apiFetcher({
      route: "/auction/getHomePageItems",
      method: "POST",
      options: {
        body: {
          pageNumber,
          pageSize
        }
      },
      signal
    });

    console.log(response, "response");

    if (response.status === 200) {
      if (!response.data || typeof response.data !== "object") {
        console.log("Response data is not an object");
        return {
          rStatus: "error",
          message: "Data is not an object"
        };
      }
      console.log(response.data, "response.data");
      if (
        !("featuredProducts" in response.data) ||
        !response.data.featuredProducts ||
        !Array.isArray(response.data.featuredProducts)
      ) {
        console.log("featuredProducts is not an array");
        return {
          rStatus: "error",
          message: "featuredProducts is not an array"
        };
      }

      if (
        !("products" in response.data) ||
        !response.data.products ||
        !Array.isArray(response.data.products)
      ) {
        console.log("products is not an array");
        return {
          rStatus: "error",
          message: "products is not an array"
        };
      }

      if (
        !("totalsProducts" in response.data) ||
        typeof response.data.totalsProducts !== "number"
      ) {
        console.log("totalsProducts is not a number");
        return {
          rStatus: "error",
          message: "totalsProducts is not a number"
        };
      }

      if (
        !response.data.featuredProducts.every(isShortProduct) ||
        !response.data.products.every(isShortProduct)
      ) {
        console.log("featuredProducts or products is not a ShortProduct");
        return {
          rStatus: "error",
          message: "featuredProducts or products is not a ShortProduct"
        };
      }

      // Identify the next major auction
      const currentDateTime = new Date();
      const majorAuctions = response.data.products.filter(
        (product) => product.categoryID === 0 // Example condition to identify major auctions
      );
      const nextMajorAuction = majorAuctions.reduce(
        (closest, product) => {
          const auctionTime = new Date(product.auctionTime);
          if (
            auctionTime > currentDateTime &&
            (!closest || auctionTime < new Date(closest.auctionTime))
          ) {
            return product;
          }
          return closest;
        },
        null as ShortProduct | null
      );

      if (nextMajorAuction) {
        localStorage.setItem(
          "nextMajorAuction",
          JSON.stringify(nextMajorAuction)
        );
      }

      return {
        rStatus: "success",
        rData: {
          featuredProducts: response.data.featuredProducts,
          products: response.data.products,
          nextMajorAuction: nextMajorAuction || undefined,
          totalProducts: response.data.totalsProducts
        }
      };
    }

    console.log("An error occurred");

    return {
      rStatus: "error",
      message: "An error occurred"
    };
  } catch (error) {
    return { rStatus: "error", message: "An error occurred" };
  }
}

export async function fetchAllProductsByCategoryID(categoryID: number): Promise<
  | {
      rStatus: "success";
      rData: {
        featuredProducts: ShortProduct[];
        products: ShortProduct[];
      };
    }
  | { rStatus: "error"; message: string }
> {
  try {
    const response = await apiFetcher({
      route: "/department/get",
      method: "POST",
      options: {
        body: {
          departmentID: categoryID
        }
      }
    });

    console.log(response, "response");

    if (response.status === 200) {
      if (!response.data || typeof response.data !== "object") {
        console.log("Response data is not an object");
        return {
          rStatus: "error",
          message: "Data is not an object"
        };
      }
      console.log(response.data, "response.data");
      if (
        !("featuredProducts" in response.data) ||
        !response.data.featuredProducts ||
        !Array.isArray(response.data.featuredProducts)
      ) {
        console.log("featuredProducts is not an array");
        return {
          rStatus: "error",
          message: "featuredProducts is not an array"
        };
      }

      if (
        !("products" in response.data) ||
        !response.data.products ||
        !Array.isArray(response.data.products)
      ) {
        console.log("products is not an array");
        return {
          rStatus: "error",
          message: "products is not an array"
        };
      }

      if (
        !response.data.featuredProducts.every(isShortProduct) ||
        !response.data.products.every(isShortProduct)
      ) {
        console.log("featuredProducts or products is not a ShortProduct");
        return {
          rStatus: "error",
          message: "featuredProducts or products is not a ShortProduct"
        };
      }

      return {
        rStatus: "success",
        rData: response.data as {
          featuredProducts: ShortProduct[];
          products: ShortProduct[];
        }
      };
    }

    console.log("An error occurred");

    return {
      rStatus: "error",
      message: "An error occurred"
    };
  } catch (error) {
    return { rStatus: "error", message: "An error occurred" };
  }
}

export async function fetchAllProductsByCategory(categoryID: number): Promise<
  | {
      rStatus: "success";
      rData: {
        featuredProducts: ShortProduct[];
        products: ShortProduct[];
      };
    }
  | { rStatus: "error"; message: string }
> {
  try {
    const response = await apiFetcher({
      route: "/category/get",
      method: "POST",
      options: {
        body: {
          categoryID: categoryID
        }
      }
    });

    console.log(response, "response");

    if (response.status === 200) {
      if (!response.data || typeof response.data !== "object") {
        console.log("Response data is not an object");
        return {
          rStatus: "error",
          message: "Data is not an object"
        };
      }
      console.log(response.data, "response.data");
      if (
        !("featuredProducts" in response.data) ||
        !response.data.featuredProducts ||
        !Array.isArray(response.data.featuredProducts)
      ) {
        console.log("featuredProducts is not an array");
        return {
          rStatus: "error",
          message: "featuredProducts is not an array"
        };
      }

      if (
        !("products" in response.data) ||
        !response.data.products ||
        !Array.isArray(response.data.products)
      ) {
        console.log("products is not an array");
        return {
          rStatus: "error",
          message: "products is not an array"
        };
      }

      if (
        !response.data.featuredProducts.every(isShortProduct) ||
        !response.data.products.every(isShortProduct)
      ) {
        console.log("featuredProducts or products is not a ShortProduct");
        return {
          rStatus: "error",
          message: "featuredProducts or products is not a ShortProduct"
        };
      }

      return {
        rStatus: "success",
        rData: response.data as {
          featuredProducts: ShortProduct[];
          products: ShortProduct[];
        }
      };
    }

    console.log("An error occurred");

    return {
      rStatus: "error",
      message: "An error occurred"
    };
  } catch (error) {
    return { rStatus: "error", message: "An error occurred" };
  }
}

export async function fetchMajorAuction(): Promise<
  | {
      rStatus: "success";
      rData: ShortProduct;
    }
  | { rStatus: "error"; message: string }
> {
  try {
    const response = await apiFetcher({
      route: "/auction/getMajor",
      method: "POST",
      options: {
        body: {}
      }
    });

    console.log(response, "response");
    if (response.status === 200) {
      if (
        response.data &&
        typeof response.data === "object" &&
        isShortProduct(response.data)
      ) {
        return {
          rStatus: "success",
          rData: response.data
        };
      }
    }
    return {
      rStatus: "error",
      message: "An error occurred"
    };
  } catch (error) {
    return { rStatus: "error", message: "An error occurred" };
  }
}

export async function fetchAllCategories(): Promise<
  | {
      rStatus: "success";
      rData: Category[];
    }
  | { rStatus: "error"; message: string }
> {
  try {
    const response = await apiFetcher({
      route: "/category/getAll",
      method: "POST",
      options: {
        body: {}
      }
    });

    console.log(response, "response");

    if (response.status === 200) {
      if (Array.isArray(response.data) && response.data.every(isCategory)) {
        return {
          rStatus: "success",
          rData: response.data
        };
      }
      return {
        rStatus: "error",
        message: "An error occurred"
      };
    }

    return { rStatus: "error", message: "An error occurred" };
  } catch (error) {
    return { rStatus: "error", message: "An error occurred" };
  }
}

export async function fetchProductById(productId: number): Promise<
  | {
      rStatus: "success";
      rData: FullProduct;
    }
  | { rStatus: "error"; message: string }
  | { rStatus: "notFound" }
> {
  try {
    const response = await apiFetcher({
      route: "/auction/get",
      method: "POST",
      options: {
        body: {
          auctionID: productId
        }
      }
    });

    console.log(response, "response");

    if (response.status === 200) {
      if (
        response.data &&
        typeof response.data === "object" &&
        isFullProduct(response.data)
      ) {
        return {
          rStatus: "success",
          rData: response.data
        };
      }

      return { rStatus: "error", message: "An error occurred" };
    }

    if (response.status === 204 || response.status === 209) {
      return { rStatus: "notFound" };
    }

    if (response.status === 404) {
      return { rStatus: "error", message: "Route Not Found" };
    }

    return { rStatus: "error", message: "An error occurred" };
  } catch (error) {
    return { rStatus: "error", message: "An error occurred" };
  }
}

// /department/getAll
export async function fetchAllDepartments(): Promise<
  | {
      rStatus: "success";
      rData: Department[];
    }
  | { rStatus: "error"; message: string }
> {
  try {
    const response = await apiFetcher({
      route: "/departments/get",
      method: "POST",
      options: {
        body: {}
      }
    });

    console.log(response, "response");

    if (response.status === 200) {
      if (Array.isArray(response.data) && response.data.every(isDepartment)) {
        return {
          rStatus: "success",
          rData: response.data
        };
      }
      Swal.fire({
        icon: "error",
        title: i18n.t("Incorrect Data"),
        html:
          `<strong class="font-extrabold">${i18n.t("Data for departments is not correct")}</strong>` +
          systemErrorAlertText(),
        confirmButtonText: i18n.t("OK"),
        customClass: {
          popup: "!rounded-lg",
          confirmButton:
            "!bg-secondary !rounded-full !outline-none !border-none !shadow-none"
        }
      });
      return {
        rStatus: "error",
        message: "An error occurred"
      };
    }

    return { rStatus: "error", message: "An error occurred" };
  } catch (error) {
    return { rStatus: "error", message: "An error occurred" };
  }
}

export async function genericFetchAllProducts(
  params:
    | { type: "homepage"; pageNumber: number; pageSize: number }
    | { type: "category"; departmentID: number | undefined; filter: string }
    | { type: "favs" }
    | { type: "categoryFilter" }
    | { type: "openAuctions" },
  signal?: AbortSignal
): Promise<
  | {
      rStatus: "success";
      rData: {
        featuredProducts: ShortProduct[];
        products: ShortProduct[];
        totalProducts?: number;
      };
    }
  | { rStatus: "error"; message: string }
> {
  console.log(params, "params");
  try {
    let response;
    if (params.type === "homepage") {
      response = await fetchAllProductsHomepage(
        params.pageSize,
        params.pageNumber,
        signal
      );
    } else if (params.type === "category") {
      if (!params.departmentID) {
        return { rStatus: "error", message: "Invalid departmentID" };
      }
      response = await fetchAllProductsByCategoryID(params.departmentID);
    } else if (params.type === "favs") {
      response = await fetchAllProductsFavs();
    } else if (params.type === "categoryFilter") {
      response = await fetchAllProductsByCategory(0); //SEND CARD ID
    } else if (params.type === "openAuctions") {
      const initialResponse = await fetchUserOpenAuctionsData();
      if (initialResponse.rStatus === "error") {
        return initialResponse;
      }
      return {
        rStatus: "success",
        rData: {
          featuredProducts: [],
          products: initialResponse.rData
        }
      };
    } else {
      return { rStatus: "error", message: "Invalid type" };
    }

    console.log(response, "response");

    return response;
  } catch (error) {
    return { rStatus: "error", message: "An error occurred" };
  }
}

export async function fetchAllWinners(
  pageSize: number,
  pageNumber: number
): Promise<
  | {
      rStatus: "success";
      rData: {
        winnerImages?: string[];
        products: WinningProduct[];
        totalProducts: number;
      };
    }
  | { rStatus: "error"; message: string }
> {
  try {
    const response = await apiFetcher({
      route: "/winnings/recent",
      method: "POST",
      options: {
        body: {
          pageNumber,
          pageSize
        }
      }
    });

    console.log(response, "response");

    if (response.status === 200) {
      if (
        response.data &&
        typeof response.data === "object" &&
        "products" in response.data &&
        Array.isArray(response.data.products) &&
        "totalProducts" in response.data &&
        typeof response.data.totalProducts === "number"
      ) {
        if (
          "winnerImages" in response.data &&
          Array.isArray(response.data.winnerImages) &&
          response.data.winnerImages.every((item) => typeof item === "string")
        ) {
          return {
            rStatus: "success",
            rData: {
              winnerImages: response.data.winnerImages,
              products: response.data.products,
              totalProducts: response.data.totalProducts
            }
          };
        }
        return {
          rStatus: "success",
          rData: {
            winnerImages: undefined,
            products: response.data.products,
            totalProducts: response.data.totalProducts
          }
        };
      }
      console.log("Types are incorrect");
      return {
        rStatus: "error",
        message: "An error occurred"
      };
    }

    if (response.status === 204) {
      return {
        rStatus: "success",
        rData: {
          winnerImages: [],
          products: [],
          totalProducts: 0
        }
      };
    }

    return { rStatus: "error", message: "An error occurred" };
  } catch (error) {
    return { rStatus: "error", message: "An error occurred" };
  }
}

export async function fetchAllProductsFavs(): Promise<
  | {
      rStatus: "success";
      rData: {
        featuredProducts: [];
        products: ShortProduct[];
      };
    }
  | { rStatus: "error"; message: string }
> {
  try {
    const response = await apiFetcher({
      route: "/favorite/get",
      method: "POST",
      options: {
        body: {}
      }
    });

    console.log(response, "response");

    if (response.status === 200) {
      if (!response.data || !Array.isArray(response.data)) {
        console.log("products is not an array");
        return {
          rStatus: "error",
          message: "products is not an array"
        };
      }

      if (!response.data.every(isShortProduct)) {
        console.log("Products is not a ShortProduct");
        return {
          rStatus: "error",
          message: "Products is not a ShortProduct"
        };
      }

      const returnData = {
        featuredProducts: [],
        products: response.data
      };

      return {
        rStatus: "success",
        rData: returnData as {
          featuredProducts: [];
          products: ShortProduct[];
        }
      };
    }

    if (response.status === 204) {
      return {
        rStatus: "success",
        rData: {
          featuredProducts: [],
          products: []
        }
      };
    }

    console.log("An error occurred");

    return {
      rStatus: "error",
      message: "An error occurred"
    };
  } catch (error) {
    return { rStatus: "error", message: "An error occurred" };
  }
}

export async function fetchAllProductsBySearch({
  searchText,
  departmentID,
  categoryID,
  minPrice,
  maxPrice,
  availability,
  ratings,
  bidType,
  status,
  sort,
  pageNumber,
  pageSize,
  signal
}: {
  searchText: Readonly<string | null>;
  departmentID: Readonly<number | null>;
  categoryID: Readonly<number[] | null>;
  minPrice: Readonly<number | null>;
  maxPrice: Readonly<number | null>;
  availability: Readonly<number[] | null>;
  ratings: Readonly<number[] | null>;
  bidType: Readonly<string[] | null>;
  status: Readonly<string[] | null>;
  sort: Readonly<AllURLFilterValues["sort"]>;
  pageNumber: number | undefined;
  pageSize: number | undefined;
  signal?: AbortSignal;
}): Promise<
  | {
      rStatus: "success";
      rData: {
        filters: SearchFilterOptions;
        products: {
          found: ShortProduct[];
          suggested: ShortProduct[];
        };
        totalProducts: number;
      };
    }
  | { rStatus: "error"; message: string }
> {
  try {
    const response = await apiFetcher({
      route: "/search/auction",
      method: "POST",
      options: {
        body: {
          pageNumber: pageNumber,
          pageSize: pageSize,
          departmentID: departmentID,
          categoryID: categoryID,
          minPrice: minPrice,
          maxPrice: maxPrice,
          availability: availability,
          ratings: ratings,
          bidType: bidType,
          status: status,
          searchText: searchText,
          sort: sort
        }
      },
      signal
    });

    console.log(response);

    if (response.status === 200) {
      if (
        !response.data ||
        typeof response.data !== "object" ||
        !("products" in response.data) ||
        !response.data.products ||
        typeof response.data.products !== "object" ||
        !("found" in response.data.products) ||
        !Array.isArray(response.data.products.found) ||
        !response.data.products.found.every(isShortProduct) ||
        !("suggested" in response.data.products) ||
        !Array.isArray(response.data.products.suggested) ||
        !response.data.products.suggested.every(isShortProduct)
      ) {
        console.log("Products is not a ShortProduct");
        return {
          rStatus: "error",
          message: "Products is not a ShortProduct"
        };
      }

      if (
        !("filters" in response.data) ||
        typeof response.data.filters !== "object"
      ) {
        console.log("Filters is not in the response");
        return {
          rStatus: "error",
          message: "Filters is not in the response"
        };
      }

      if (!isResponseSearchFilterOptions(response.data.filters)) {
        console.log("Filters is not in the correct format");
        return {
          rStatus: "error",
          message: "Filters is not in the correct format"
        };
      }

      let totalProducts = 0;

      if (
        "totalProducts" in response.data &&
        typeof response.data.totalProducts === "number"
      ) {
        totalProducts = response.data.totalProducts;
      }

      return {
        rStatus: "success",
        rData: {
          filters: {
            category: response.data.filters.category,
            available: response.data.filters.available,
            rating: response.data.filters.rating,
            bidType: response.data.filters.bidType,
            status: response.data.filters.status,
            price: response.data.filters.price
          },
          products: {
            found: response.data.products.found,
            suggested: response.data.products.suggested
          },
          totalProducts: totalProducts
        }
      };
    }

    if (response.status === 204) {
      return {
        rStatus: "success",
        rData: {
          filters: {
            category: [],
            available: [],
            rating: [],
            bidType: [],
            status: [],
            price: 0
          },
          products: {
            found: [],
            suggested: []
          },
          totalProducts: 0
        }
      };
    }

    console.log("An error occurred");

    return {
      rStatus: "error",
      message: "An error occurred"
    };
  } catch (error) {
    return { rStatus: "error", message: "An error occurred" };
  }
}

// /auction/bidBuddy
export async function sendNewBidBuddyStatus({
  active,
  auctionID,
  buddyAmount,
  signal
}: {
  active: boolean;
  auctionID: Readonly<number>;
  buddyAmount: number;
  signal?: AbortSignal;
}): Promise<
  | {
      rStatus: "success";
    }
  | { rStatus: "error"; message: string }
> {
  try {
    const response = await apiFetcher({
      route: "/auction/bidBuddy",
      method: "POST",
      options: {
        body: {
          active: active,
          auctionID: auctionID,
          buddyAmount: buddyAmount
        }
      },
      signal
    });

    console.log(response, "response");

    if (response.status === 200 || response.status === 202) {
      return {
        rStatus: "success"
      };
    }

    return { rStatus: "error", message: "An error occurred" };
  } catch (error) {
    return { rStatus: "error", message: "An error occurred" };
  }
}
